import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Button,
  LinearProgress,
  Chip,
  Divider,
  Avatar,
  CardActions,
  Paper,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../../hooks/useAuth0";
import patientService from "../../services/patientService";
import EnhancedTrialCard from '../../components/ClinicalTrials/EnhancedTrialCard';
// Icons
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import TimelineIcon from "@mui/icons-material/Timeline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HistoryIcon from "@mui/icons-material/History";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MedicationIcon from "@mui/icons-material/Medication";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScienceIcon from "@mui/icons-material/Science";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StorageIcon from "@mui/icons-material/Storage";
import api from '../../services/api';
import { useSubscription } from '../../hooks/useSubscription';

// Epic Health Records Connect
import ConnectEpicButton from '../../components/ConnectEpicButton';

const PatientDashboard = () => {
  console.log("🔵 USING FINAL DASHBOARD VERSION"); // Debug marker

  const navigate = useNavigate();
  const { user } = useAuth0();
  const [profileData, setProfileData] = useState(null);
  const [matchedTrials, setMatchedTrials] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [loading, setLoading] = useState(true);
  const [trialsLoading, setTrialsLoading] = useState(true);
  const [showAllTrials, setShowAllTrials] = useState(false);
  const [redirectingToProfile, setRedirectingToProfile] = useState(false);
  const { refetch: refetchSubscription } = useSubscription();
  const [showPremiumToast, setShowPremiumToast] = useState(false);
   

  useEffect(() => {
  loadProfile();
  loadMatchedTrials();

  // Check if returning from Stripe payment
  const params = new URLSearchParams(window.location.search);
  if (params.get('subscription') === 'success') {
  setShowPremiumToast(true);
  window.history.replaceState({}, '', '/dashboard');
  setTimeout(() => setShowPremiumToast(false), 6000);
  // Reload after 2 seconds — forces all hooks to refetch fresh data
  setTimeout(() => window.location.reload(), 2000);
}
}, []);

  const loadProfile = async () => {
    try {
      const response = await patientService.getMyProfile();

      if (response.success && response.data) {
        // Redirect to profile setup whenever the profile isn't complete —
        // not only when completion is exactly 0%. This covers accounts
        // that got a Patient record auto-provisioned with a few fields
        // pre-filled (e.g. from a social login), which otherwise slipped
        // past a stricter "=== 0" check and landed on a half-populated
        // dashboard instead of profile setup.
        if (!response.data.profileComplete) {
          console.log("🔵 Profile incomplete, redirecting to profile...");
          setRedirectingToProfile(true);
          navigate("/profile");
          return;
        }

        setProfileData(response.data);
      } else {
        // getMyProfile() resolves with { success: false } rather than
        // throwing — this is the real path hit for a 404 (no Patient
        // record yet), which covers social login and Auth0-hosted
        // email/password signup, neither of which create a Patient
        // record on their own. Make sure a minimal one exists (using
        // whatever Auth0 already gave us client-side) before sending
        // the user to /profile, so that page always has something to
        // load and edit instead of hitting the same 404 itself.
        console.log("🔵 No profile found, initializing minimal profile...");
        setRedirectingToProfile(true);
        try {
          await patientService.initializeProfile({
            email: user?.email,
            firstName: user?.given_name,
            lastName: user?.family_name,
          });
        } catch (initError) {
          console.error("Error initializing profile:", initError);
          // Proceed to /profile regardless — worst case the profile
          // page's own load hits the same 404 and the user can still
          // fill the form in manually.
        }
        navigate("/profile");
        return;
      }
    } catch (error) {
      // Safety net for a genuinely unexpected error (network failure,
      // etc.) — still get the user to somewhere useful rather than a
      // dead-end error screen.
      console.error("Unexpected error loading profile:", error);
      setRedirectingToProfile(true);
      navigate("/profile");
      return;
    } finally {
      setLoading(false);
    }
  };

const loadMatchedTrials = async () => {
  try {
    setTrialsLoading(true);
    
    const response = await api.get('/v1/patient-matching/my-matches');
    const data = response.data;
    
    console.log("✅ Matched trials loaded:", data);

    const trialsWithMatchData = (data.matchedTrials || []).map(match => ({
      ...match.trial,
      matchScore: match.matchScore,
      matchReasons: match.matchReasons,
      source: "internal",
    }));

    setMatchedTrials(trialsWithMatchData);
    setTotalMatches(data.totalMatches || 0);

  } catch (error) {
    console.error("❌ Error loading matched trials:", error);
    setMatchedTrials([]);
    setTotalMatches(0);
  } finally {
    setTrialsLoading(false);
  }
};

  if (loading || redirectingToProfile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: "center" }}>
          Loading your dashboard...
        </Typography>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error Loading Dashboard</AlertTitle>
          Unable to load your profile data. Please try again or contact support.
        </Alert>
      </Container>
    );
  }

  const completion = profileData.profileCompletionPercentage || 0;
  const isComplete = profileData.profileComplete || false;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {showPremiumToast && (
  <Alert
    severity="success"
    icon={<span style={{ fontSize: '24px' }}>✨</span>}
    onClose={() => setShowPremiumToast(false)}
    sx={{
      mb: 3,
      background: 'linear-gradient(135deg, #1A5EA8, #0B2545)',
      color: 'white',
      fontWeight: '600',
      fontSize: '16px',
      '& .MuiAlert-icon': { color: 'white' },
      '& .MuiAlert-action': { color: 'white' },
    }}
  >
    🎉 Welcome to Care Copilot Premium! Unlimited questions and lab result 
    interpretation are now unlocked. Open the 🤝 widget to get started!
  </Alert>
)}
      {/* ========== HEADER WITH STATUS BADGE ========== */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              src={user?.picture}
              alt={profileData.fullName}
              sx={{
                width: 80,
                height: 80,
                border: "3px solid",
                borderColor: isComplete ? "success.main" : "warning.main",
              }}
            >
              {profileData.firstName?.[0]}{profileData.lastName?.[0]}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Welcome back, {profileData.firstName}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {profileData.city && profileData.state
                ? `${profileData.city}, ${profileData.state}`
                : "Update your location in profile"}
            </Typography>
          </Grid>
          <Grid item>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                background: isComplete
                  ? "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
                  : "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                color: "white",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {isComplete ? (
                  <CheckCircleIcon sx={{ fontSize: 28 }} />
                ) : (
                  <WarningIcon sx={{ fontSize: 28 }} />
                )}
                <Typography variant="h6" fontWeight="bold">
                  {isComplete ? "Active" : "Pending"}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {trialsLoading ? "Loading..." : `${totalMatches} Trials Matched`}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate("/profile")}
                sx={{
                  mt: 1,
                  borderColor: "white",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                Edit Profile
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ========== PROFILE COMPLETION ALERT ========== */}
      {!isComplete && completion < 100 && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => navigate("/profile")}
              >
                Complete Now
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: "bold" }}>
              Profile Incomplete ({completion}%)
            </AlertTitle>
            Complete your profile to receive accurate clinical trial matches.
          </Alert>
        </Box>
      )}

      {/* ========== SUCCESS MESSAGE ========== */}
      {isComplete && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="success">
            <AlertTitle sx={{ fontWeight: "bold" }}>Profile Complete! ✅</AlertTitle>
            All required fields completed. You're eligible for personalized clinical trial matching.
          </Alert>
        </Box>
      )}

      {/* ========== KEY METRICS CARDS ========== */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Profile Completion */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              cursor: "pointer",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
            onClick={() => navigate("/profile")}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TimelineIcon sx={{ fontSize: 40, color: "primary.main", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Profile
                </Typography>
              </Box>
              <Typography variant="h3" color="primary" gutterBottom>
                {completion}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completion}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1,
                  backgroundColor: "rgba(102, 126, 234, 0.1)",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {isComplete ? "Complete!" : `${Math.round((100 - completion) / 100 * 21)} fields remaining`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Matched Trials */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: "100%",
              opacity: isComplete ? 1 : 0.7,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SearchIcon sx={{ fontSize: 40, color: "success.main", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Matched Trials
                </Typography>
              </Box>
              <Typography
                variant="h3"
                color={isComplete ? "success.main" : "text.disabled"}
              >
                {trialsLoading ? "..." : totalMatches}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isComplete
                  ? `${totalMatches} trial${totalMatches !== 1 ? "s" : ""} match your profile`
                  : "Complete profile to see matches"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Status */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                {isComplete ? (
                  <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", mr: 1 }} />
                ) : (
                  <WarningIcon sx={{ fontSize: 40, color: "warning.main", mr: 1 }} />
                )}
                <Typography variant="h6" fontWeight="bold">
                  Status
                </Typography>
              </Box>
              <Typography
                variant="h3"
                color={isComplete ? "success.main" : "warning.main"}
              >
                {isComplete ? "Active" : "Pending"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isComplete ? "Ready for trial matching" : "Complete your profile"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

         <Grid item>
                    <Button variant="contained" onClick={() => navigate("/my-health/find-provider")}
                      startIcon={<LocalHospitalIcon />}
                      sx={{
                        background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white",
                        fontWeight: 700, fontSize: 15, padding: "12px 28px", borderRadius: 2,
                        boxShadow: "0 4px 16px rgba(102,126,234,0.4)"
                      }}>
                      🏥 Find Provider Near Me
                    </Button>
                  </Grid>

        {/* Connect Epic Health Records */}
        <Grid item>
          <ConnectEpicButton />
        </Grid>

      </Grid>

      {/* ========== MATCHED TRIALS SECTION ========== */}
      {isComplete && (
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              🎯 Your Matched Trials {totalMatches > 0 && `(${totalMatches})`}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => setShowAllTrials(!showAllTrials)}
              sx={{
                background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              }}
            >
              {showAllTrials ? "Show Less ▲" : "Show All Trials ▼"}
            </Button>
          </Box>

          {trialsLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading matched trials...
              </Typography>
            </Box>
          ) : matchedTrials.length === 0 ? (
            <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
              <ScienceIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Matching Trials Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We're constantly adding new trials. Check back soon or update your profile for better matches.
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate("/profile")}
              >
                Update Profile
              </Button>
            </Paper>
          ) : (
           <Box>
  {(showAllTrials ? matchedTrials : matchedTrials.slice(0, 3))
    .filter(trial => trial && trial.nctId)
    .map((trial, index) => (
      <EnhancedTrialCard
        key={trial.nctId || index}
        trial={trial}
        matchScore={trial.matchScore}
        patientProfile={{
          firstName: profileData?.firstName || '',
          lastName: profileData?.lastName || '',
          email: profileData?.email || '',
          phoneNumber: profileData?.phoneNumber || '',
          ckdStage: profileData?.ckdStage || '',
          dialysisModality: profileData?.dialysisModality || '',
          city: profileData?.city || '',
          state: profileData?.state || '',
        }}
      />
    ))}
</Box>
          )}
        </Box>
      )}

      {/* ========== MY HEALTH TOOLS ========== */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          My Health Tools
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<EventNoteIcon sx={{ fontSize: 48, color: "#1A5EA8" }} />}
              title="Daily Log"
              description="Track daily medications"
              onClick={() => isComplete && navigate(`/patients/me/daily-intake`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<HistoryIcon sx={{ fontSize: 48, color: "#0B2545" }} />}
              title="Med History"
              description="View past records"
              onClick={() => isComplete && navigate(`/patients/me/daily-intake/history`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<SearchIcon sx={{ fontSize: 48, color: "#43e97b" }} />}
              title="Browse All Trials"
              description="Search clinical trials"
              onClick={() => navigate("/amyloid/clinicaltrialsearch")}
              disabled={false}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<MonitorHeartIcon sx={{ fontSize: 48, color: "#fa709a" }} />}
              title="Symptom Tracker"
              description="Track symptoms"
              onClick={() => isComplete && navigate(`/tools/lab-tracker`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<FolderOpenIcon sx={{ fontSize: 48, color: "#4facfe" }} />}
              title="Documents"
              description="Medical records"
              onClick={() => isComplete && navigate(`/tools/documents`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<MedicationIcon sx={{ fontSize: 48, color: "#f093fb" }} />}
              title="Medications"
              description="Manage medications"
              onClick={() => isComplete && navigate(`/tools/medications`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<CalendarTodayIcon sx={{ fontSize: 48, color: "#11998e" }} />}
              title="Calendar"
              description="Trial appointments"
              onClick={() => isComplete && navigate(`/tools/calendar`)}
              disabled={!isComplete}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <HealthToolCard
              icon={<AttachMoneyIcon sx={{ fontSize: 48, color: "#ffa726" }} />}
              title="Cost Calculator"
              description="Estimate costs"
              onClick={() => isComplete && navigate(`/tools/cost-calculator`)}
              disabled={!isComplete}
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

// ========== HEALTH TOOL CARD COMPONENT ==========
const HealthToolCard = ({ icon, title, description, onClick, disabled }) => (
  <Card
    elevation={2}
    sx={{
      height: "100%",
      cursor: disabled ? "default" : "pointer",
      transition: "all 0.3s",
      opacity: disabled ? 0.6 : 1,
      "&:hover": disabled ? {} : { transform: "translateY(-4px)", boxShadow: 4 },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ textAlign: "center", py: 3 }}>
      {icon}
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      {disabled && (
        <Chip label="Complete Profile" size="small" color="warning" sx={{ mt: 1 }} />
      )}
    </CardContent>
  </Card>
);

// ========== TRIAL CARD COMPONENT ==========
const TrialCard = ({ trial }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || "";
    if (statusUpper.includes("RECRUITING")) return "success";
    if (statusUpper.includes("ACTIVE")) return "warning";
    if (statusUpper.includes("COMPLETED")) return "info";
    if (statusUpper.includes("NOT YET")) return "error";
    return "default";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ") || "Unknown";
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleBookTravel = () => {
    const trialLocation = trial.locations || "Trial Location";
    const trialName = trial.title || trial.nctId;
    const sanoGridUrl = `https://sanogrid.com?trial=${encodeURIComponent(trialName)}&location=${encodeURIComponent(trialLocation)}`;
    window.open(sanoGridUrl, "_blank");
  };

  const getMatchColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "info";
  };

  const matchScore = trial.matchScore || 0;
  const matchReasons = trial.matchReasons || [];
  const hasMatchData = matchScore > 0;

  return (
    <Card
      elevation={3}
      sx={{
        "&:hover": { boxShadow: 6 },
        border: `3px solid`,
        borderColor: hasMatchData ? `${getMatchColor(matchScore)}.main` : "#2e7d32",
      }}
    >
      <CardContent>
        {/* Match Score Badge */}
        {hasMatchData && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                mb: 2,
              }}
            >
              <Chip
                icon={<CheckCircleIcon />}
                label={`${matchScore}% Match`}
                color={getMatchColor(matchScore)}
                sx={{ fontWeight: "bold", fontSize: "1rem", px: 1 }}
              />
              <Chip
                label={formatStatus(trial.status)}
                color={getStatusColor(trial.status)}
                size="small"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={matchScore}
                color={getMatchColor(matchScore)}
                sx={{ height: 10, borderRadius: 2 }}
              />
            </Box>
          </>
        )}

        {!hasMatchData && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Chip
              icon={<StorageIcon />}
              label="Our Database"
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={formatStatus(trial.status)}
              color={getStatusColor(trial.status)}
              size="small"
            />
          </Box>
        )}

        {/* Trial Title */}
        <Typography variant="h6" gutterBottom fontWeight="bold">
          {trial.title}
        </Typography>

        {/* NCT ID */}
        <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: "bold" }}>
          NCT ID: {trial.nctId}
        </Typography>

        {/* Trial Details Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">
              Phase
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {trial.phase || "N/A"}
            </Typography>
          </Grid>

          {trial.sponsor && (
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Sponsor
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.sponsor}
              </Typography>
            </Grid>
          )}

          {trial.ckdStage && (
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Stage
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.ckdStage}
              </Typography>
            </Grid>
          )}

          {trial.targetOrgan && (
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Target Organ
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.targetOrgan}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Match Reasons */}
        {hasMatchData && matchReasons && matchReasons.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom color="success.main" fontWeight="bold">
              Why This Trial Matches:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {matchReasons.map((reason, idx) => (
                <Chip
                  key={idx}
                  icon={<CheckCircleIcon />}
                  label={reason}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </>
        )}

        {/* Brief Summary */}
        {trial.briefSummary && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {trial.briefSummary.length > 250
                ? `${trial.briefSummary.substring(0, 250)}...`
                : trial.briefSummary}
            </Typography>
          </>
        )}
      </CardContent>

      {/* Card Actions */}
      <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleExpandClick}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          fullWidth
        >
          {expanded ? "Hide Details" : "View Details"}
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleBookTravel}
          startIcon={<FlightTakeoffIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
            color: "white",
            fontWeight: "bold",
            "&:hover": {
              background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)",
            },
          }}
        >
          Book Travel via sanogrid
        </Button>
      </CardActions>

      {/* Expandable Details Section */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent sx={{ bgcolor: "#f5f5f5" }}>
          <Grid container spacing={3}>
            {/* Detailed Description */}
            {trial.detailedDescription && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Detailed Description
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {trial.detailedDescription}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Eligibility Criteria */}
            {trial.eligibilityCriteria && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Eligibility Criteria
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {trial.eligibilityCriteria}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Locations */}
            {trial.locations && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Trial Locations
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2">
                    {trial.locations}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* {trial.nctId && (
                  <Button
                    variant="contained"
                    color="primary"
                    href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon />}
                    fullWidth
                  >
                    View on ClinicalTrials.gov
                  </Button>
                )} */}

                {/* <Button
                  variant="contained"
                  onClick={handleBookTravel}
                  startIcon={<FlightTakeoffIcon />}
                  fullWidth
                  sx={{
                    background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)",
                    },
                  }}
                >
                  Book Travel via SanoGrid
                </Button> */}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default PatientDashboard;